import { RemoveMediaFromAllFoldersRequestParams, RemoveMediaFromAllFoldersResponseParams } from "../types";
import { useState } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { Button, Modal, ModalActions, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { baseUrl, postKmsData, translate, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { ToastMessages } from "@kaltura/mediaspace-shared-ui";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface RemoveFromAllFoldersModalProps {
    /** boolean flag to indicate the modal is currently open */
    isOpen: boolean;
    /** callback to execute when clicking cancel */
    onClose: () => void;
    /** entry ids to remove from all folders they are currently associated with */
    entryIds: string[];
    /** callback to execute after successful remove from all folders request */
    onSubmitEnd?: () => void;
}

const baseClassName = "kms-ds-folders-remove-from-all-folders-modal";

/**
 * remove entries from all folders modal
 * handles removing media from all folders it is currently associated with
 * displays confirmation and handles the remove action
 * used in my-media when dragging and dropping entries on the Unfiled media folder
 */
export function RemoveFromAllFoldersModal({ onClose, isOpen, entryIds, onSubmitEnd }: RemoveFromAllFoldersModalProps) {
    const [isProcessing, setIsProcessing] = useState(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const handleRemoveFromAllFolders = async () => {
        sendButtonAnalytics(
            "Remove from all folders - Remove",
            ButtonClickAnalyticsType.DELETE,
            entryIds.length === 1 ? entryIds[0] : undefined
        );
        setIsProcessing(true);
        const requestData: RemoveMediaFromAllFoldersRequestParams = {
            entryIds: entryIds,
        };
        try {
            const result: RemoveMediaFromAllFoldersResponseParams = await postKmsData(
                `${baseUrl}/folders/entry/remove-from-all`,
                requestData
            );
            setIsProcessing(false);

            if (result.successMessages && result.successMessages.length > 0) {
                // max one success message is returned
                showToast({
                    severity: "success",
                    message: result.successMessages[0],
                });
                onSubmitEnd?.();
            }
            // the server returns either successMessages or errorMessages - not both
            else {
                if (result.errorMessages && result.errorMessages.length > 0) {
                    showToast({
                        severity: "error",
                        message: (
                            <ToastMessages
                                title={translate("Failed to remove the following media:")}
                                messages={result.errorMessages}
                            />
                        ),
                    });
                }
            }
        }
        catch (e) {
            setIsProcessing(false);
            showToast({
                severity: "error",
                message: translate("Failed to remove media from all folders"),
            });
        }
    };

    const handleClose = () => {
        sendButtonAnalytics(
            "Remove from all folders - Cancel",
            ButtonClickAnalyticsType.CLOSE,
            entryIds.length === 1 ? entryIds[0] : undefined
        );
        onClose();
    };

    return (
        <Modal
            classes={{
                root: `${baseClassName}-root`,
                paper: `${baseClassName}-paper`,
            }}
            open={isOpen}
            fullWidth={true}
        >
            <ModalTitle className={`${baseClassName}-title`}>{translate("Remove from all folders")}</ModalTitle>
            <ModalContent className={`${baseClassName}-content`}>
                <Typography variant={"body1"} component={"p"} align={"center"}>
                    {translatePlural(
                        "This action will remove the selected media from all folders it is currently associated with.",
                        "This action will remove the selected media from all folders they are currently associated with.",
                        [entryIds.length]
                    )}
                </Typography>
            </ModalContent>
            <ModalActions className={`${baseClassName}-actions`}>
                <Button
                    onClick={handleClose}
                    variant={"borderless"}
                    color={"secondary"}
                    className={`${baseClassName}-actions-cancel`}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    loading={isProcessing}
                    disabled={isProcessing}
                    className={`${baseClassName}-actions-submit`}
                    color={"danger"}
                    onClick={handleRemoveFromAllFolders}
                >
                    {translate("Remove from all folders")}
                </Button>
            </ModalActions>
        </Modal>
    );
}

export default RemoveFromAllFoldersModal;
